// Global Instructions Rule Applied!
import { useState, useMemo, useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../utils/errorToast.tsx'
import { updateDealCommitments } from '../../../app_modules/deals/services/dealsService.ts'
import { useFundOptions } from '../../../app_modules/global_configs/hooks/useFundOptions.ts'
// Optimistic updates removed - no longer supported
import { updateDealRecord } from '../../../state_manager/state_update_actions/deals/deal_actions.ts'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { selectDealById } from '../../../state_manager/state_update_actions/deals/selectors.ts'
import {
  TOAST_CONFIG,
  TOAST_SUCCESS_CONFIG,
  TIMING,
  generateNewKey,
  isNewEntry
} from '../FormTable/constants.ts'
import { COMMON_VALIDATION_SETS, validateAndShowErrors } from '../FormTable/validation.ts'
import { DealFields } from '../../../state_manager/data_models/deal_record.ts'
import { DealCommitmentEntryFields } from '../../../state_manager/data_models/deal_commitment_entry.ts'
import { validateDealCommitmentEntry } from '../../../state_manager/data_models/deal_commitment_entry.ts'
import { ACTION_TYPE_INSERT, ACTION_TYPE_UPDATE, ACTION_TYPE_DELETE } from '../../../state_manager/globals.ts'

export function useDealCommitmentsController({
  mode = 'new',
  initialCommitments = [],
  onChange = null,
  onSave = null,
  currencySymbol = '$',
  recordId = null
}) {
  const dispatch = useDispatch()
  const currentDeal = useSelector((state) => (recordId ? selectDealById(state, recordId) : null))
  const [commitments, setCommitments] = useState(initialCommitments)
  const [editingRowKey, setEditingRowKey] = useState(null)
  const [focusField, setFocusField] = useState(null)
  const [originalCommitmentData, setOriginalCommitmentData] = useState(null)
  const [savingRowKey, setSavingRowKey] = useState(null)
  const [deletingRowKey, setDeletingRowKey] = useState(null)
  const [fieldValues, setFieldValues] = useState({})
  const [validationErrors, setValidationErrors] = useState({}) // Track validation errors by field
  const [newlyAddedCommitments, setNewlyAddedCommitments] = useState(new Set())
  const initialCommitmentsRef = useRef(initialCommitments)

  // Debounce timeouts for amount field
  const debounceTimeouts = useRef({})

  // Helper function to transform commitment from UI format to schema format
  const transformCommitmentToSchemaFormat = (commitment, isNewCommitment = false) => {
    // Extract UUID from key - strip "new_" prefix if present
    let commitmentId = commitment.key
    if (isNewEntry(commitment.key)) {
      // For new entries, strip the "new_" prefix to get the UUID
      commitmentId = commitment.key.replace(/^new_/, '')
    }

    // Validate commitment ID is a valid UUID
    if (!commitmentId || typeof commitmentId !== 'string') {
      throw new Error('Commitment ID must be a valid UUID string')
    }

    // Ensure amount is a number (required, cannot be null)
    const amount = commitment.amount != null ? Number(commitment.amount) : null
    if (amount === null || isNaN(amount)) {
      throw new Error('Amount is required and must be a valid number')
    }
    if (amount < 0) {
      throw new Error('Amount must be a non-negative number')
    }

    // Ensure fund_id is a valid UUID string (required, cannot be null)
    const fundId = commitment.fund
    if (!fundId || typeof fundId !== 'string') {
      throw new Error('Fund ID is required and must be a valid UUID string')
    }

    return {
      [DealCommitmentEntryFields.deal_commitments__id]: commitmentId,
      [DealCommitmentEntryFields.deal_commitments__fund_id]: fundId,
      [DealCommitmentEntryFields.deal_commitments__amount]: amount,
      [DealCommitmentEntryFields.prog__deal_commitments__action]: isNewCommitment
        ? ACTION_TYPE_INSERT
        : ACTION_TYPE_UPDATE
    }
  }

  // Function to scroll table to the left and focus Add Commitment button
  const scrollToLeftAndFocus = () => {
    // Try multiple selectors to find the scrollable table container
    const selectors = [
      '.ant-table-content',
      '.ant-table-container .ant-table-content',
      '.ant-table-body',
      '.ant-table-container',
      '.commitment-table .ant-table-content',
      '.commitment-table .ant-table-container',
      '.ant-table-tbody'
    ]

    let tableContainer = null
    for (const selector of selectors) {
      const element = document.querySelector(selector)
      if (element && element.scrollWidth > element.clientWidth) {
        tableContainer = element
        break
      }
    }

    if (tableContainer) {
      // Scroll table to the left using both methods for reliability
      tableContainer.scrollTo({ left: 0, behavior: 'smooth' })
      tableContainer.scrollLeft = 0
    }

    // Focus the Add Commitment button for keyboard accessibility
    setTimeout(() => {
      const addButton = document.querySelector('[data-testid="add-commitment-button"]')
      if (addButton) {
        addButton.focus()
      }
    }, 300) // Delay to allow scroll animation to complete
  }

  // Update commitments when initialCommitments changes
  useEffect(() => {
    // Don't override local changes if we're currently editing a new commitment
    if (editingRowKey && newlyAddedCommitments.has(editingRowKey)) {
      return
    }

    // Check if the data has actually changed
    const hasChanged =
      initialCommitments.length !== initialCommitmentsRef.current.length ||
      JSON.stringify(initialCommitments) !== JSON.stringify(initialCommitmentsRef.current)

    if (hasChanged) {
      setCommitments(initialCommitments)
      initialCommitmentsRef.current = initialCommitments

      // Clear newly added tracking when re-initializing from props
      // This ensures proper API call detection after navigation
      setNewlyAddedCommitments(new Set())
    }
  }, [initialCommitments, editingRowKey, newlyAddedCommitments])

  // Fund picker gate (fund status, 2026-09-04). The gate lives HERE, not at the
  // three deal forms that render this grid, so a call site physically cannot
  // hand it an ungated list.
  //
  // scope 'any': you still book a commitment against a fund whose raise closed
  // years ago — only Cancelled and Terminated drop out.
  //
  // Every fund already on the grid is pinned. Without that, FundColumn's
  // read-mode branch finds no matching option for a since-cancelled fund and
  // falls back to rendering the raw uuid.
  const pinnedFundIds = useMemo(
    () => commitments.map((commitment) => commitment.fund).filter(Boolean),
    [commitments]
  )
  const {
    options: gatedFundOptions,
    showAll: showAllFunds,
    setShowAll: setShowAllFunds,
    hiddenCount: hiddenFundCount
  } = useFundOptions({ scope: 'any', pin: pinnedFundIds })

  // Get available fund options (excluding already selected funds)
  const getAvailableFundOptions = (excludeKey = null) => {
    const usedFundIds = commitments
      .filter((commitment) => commitment.key !== excludeKey)
      .map((commitment) => commitment.fund)
      .filter(Boolean)

    return gatedFundOptions.filter((option) => {
      // Handle both {id, name} and {value, label} formats
      const optionId = option.id ?? option.value
      return !usedFundIds.includes(optionId)
    })
  }

  const handleAddCommitment = () => {
    const newKey = generateNewKey()
    const newCommitment = {
      key: newKey,
      fund: null,
      amount: null
    }
    const updatedCommitments = [...commitments, newCommitment]
    setCommitments(updatedCommitments)

    // Track as newly added
    setNewlyAddedCommitments((prev) => new Set([...prev, newKey]))

    // For existing mode, notify parent immediately; for new mode, wait until save
    if (mode === 'existing' && onChange) {
      onChange(updatedCommitments)
    }

    // Set editing state and focus fund field
    setEditingRowKey(newKey)
    setFocusField('fund')

    // Initialize field values
    setFieldValues((prev) => ({
      ...prev,
      [`${newKey}-fund`]: null,
      [`${newKey}-amount`]: null
    }))
  }

  const handleRemoveCommitment = async (key) => {
    try {
      setDeletingRowKey(key)

      if (editingRowKey === key) {
        setEditingRowKey(null)
      }

      if (mode === 'existing' && recordId) {
        // Find the commitment being deleted
        const commitmentToDelete = commitments.find((commitment) => commitment.key === key)
        const isNewCommitment = newlyAddedCommitments.has(key)

        // Remove from newlyAddedCommitments tracking if it's a new commitment
        if (isNewCommitment) {
          setNewlyAddedCommitments((prev) => {
            const newSet = new Set(prev)
            newSet.delete(key)
            return newSet
          })
        }

        // For existing mode, always call API (for both existing and new commitments)
        const updatedCommitments = commitments.filter((commitment) => commitment.key !== key)

        // Transform remaining commitments to schema format
        const schemaFormattedCommitments = updatedCommitments.map((commitment) => {
          try {
            const isNew = newlyAddedCommitments.has(commitment.key)
            return transformCommitmentToSchemaFormat(commitment, isNew)
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to transform commitment'
            throw new Error(`Commitment transformation failed: ${errorMessage}`)
          }
        })

        // If deleting an existing commitment (not a new one), add it with DELETE action
        if (commitmentToDelete && !isNewCommitment) {
          // Extract UUID from key (should already be a UUID for existing entries, but be safe)
          let deleteId = commitmentToDelete.key
          if (isNewEntry(deleteId)) {
            deleteId = deleteId.replace(/^new_/, '')
          }

          // For DELETE, use the commitment's existing values (required by schema validation)
          // Ensure amount is a number
          const deleteAmount = commitmentToDelete.amount != null ? Number(commitmentToDelete.amount) : 0
          if (isNaN(deleteAmount)) {
            throw new Error('Cannot delete commitment: invalid amount')
          }

          // Ensure fund_id is a valid UUID string
          const deleteFundId = commitmentToDelete.fund
          if (!deleteFundId || typeof deleteFundId !== 'string') {
            throw new Error('Cannot delete commitment: invalid fund ID')
          }

          const deleteEntry = {
            [DealCommitmentEntryFields.deal_commitments__id]: deleteId,
            [DealCommitmentEntryFields.deal_commitments__fund_id]: deleteFundId,
            [DealCommitmentEntryFields.deal_commitments__amount]: deleteAmount,
            [DealCommitmentEntryFields.prog__deal_commitments__action]: ACTION_TYPE_DELETE
          }
          schemaFormattedCommitments.push(deleteEntry)
        }

        // Validate all commitments
        try {
          schemaFormattedCommitments.forEach((commitment) => {
            validateDealCommitmentEntry(commitment)
          })
        } catch (error) {
          console.error('Validation failed for commitments:', error)
          const errorMessage = error instanceof Error ? error.message : 'Validation failed'
          showErrorToast(`Commitments validation failed: ${errorMessage}`, TOAST_CONFIG)
          throw error
        }

        const currentToken = getAccessToken(store.getState().auth)
        const result = await updateDealCommitments(recordId, schemaFormattedCommitments, currentToken)

        if (result.error) {
          showErrorToast('Failed to remove commitment', TOAST_CONFIG)
          throw new Error(result.error)
        } else {
          // Update local state - use updatedCommitments which already has the deleted commitment removed
          // The API returns result.data which includes the deleted commitment (it returns the input array),
          // so we use our local updatedCommitments instead for the correct state
          setCommitments(updatedCommitments)
          if (onChange) {
            onChange(updatedCommitments)
          }

          // Update Redux state with commitment fields
          // Use updatedCommitments (not result.data) since it has the correct state after deletion
          if (currentDeal && recordId) {
            // Map updatedCommitments to DealFields format for Redux
            const commitmentUpdates = {
              [DealFields.prog__deal_commitments]: updatedCommitments.map((commitment) => ({
                [DealCommitmentEntryFields.deal_commitments__id]: commitment.key,
                [DealCommitmentEntryFields.deal_commitments__fund_id]: commitment.fund,
                [DealCommitmentEntryFields.deal_commitments__amount]: commitment.amount
              }))
            }

            // Recalculate commitment amount
            const commitmentAmount = updatedCommitments.reduce(
              (sum, commitment) => sum + (Number(commitment.amount) || 0),
              0
            )
            commitmentUpdates[DealFields.prog__commitment_amount] = commitmentAmount

            // Update deal record for Full form display
            dispatch(
              updateDealRecord({
                dealId: recordId.toString(),
                updates: commitmentUpdates
              })
            )
          }

          toast.success('Commitment removed successfully', TOAST_CONFIG)

          // Scroll to left and focus Add Commitment button
          scrollToLeftAndFocus()
        }
      } else {
        // For new mode or new entries, update local state immediately
        const updatedCommitments = commitments.filter((commitment) => commitment.key !== key)
        setCommitments(updatedCommitments)

        // Notify parent of changes
        if (onChange) {
          onChange(updatedCommitments)
        }

        // Scroll to left and focus Add Commitment button
        scrollToLeftAndFocus()
      }
    } catch (error) {
      console.error('Failed to remove commitment:', error)
    } finally {
      setDeletingRowKey(null)
    }
  }

  const handleFieldChange = (key, field, value) => {
    // Clear focus after a delay to allow focus effects to complete
    if (focusField) {
      setTimeout(() => {
        setFocusField(null)
      }, 500) // Give time for focus to complete before clearing
    }

    // Handle debounced amount updates
    if (field === 'amount') {
      const timeoutKey = `${key}-amount`
      if (debounceTimeouts.current[timeoutKey]) {
        clearTimeout(debounceTimeouts.current[timeoutKey])
      }

      // Update validation state immediately
      setFieldValues((prev) => ({
        ...prev,
        [`${key}-${field}`]: value
      }))

      const updatedCommitments = commitments.map((commitment) => {
        if (commitment.key === key) {
          return { ...commitment, [field]: value }
        }
        return commitment
      })
      setCommitments(updatedCommitments)

      // Debounce the actual state update
      debounceTimeouts.current[timeoutKey] = setTimeout(() => {
        if (onChange) {
          onChange(updatedCommitments)
        }
      }, 300)
    } else {
      // Immediate update for non-amount fields
      const updatedCommitments = commitments.map((commitment) => {
        if (commitment.key === key) {
          return { ...commitment, [field]: value }
        }
        return commitment
      })
      setCommitments(updatedCommitments)

      // Update field values for validation
      setFieldValues((prev) => ({
        ...prev,
        [`${key}-${field}`]: value
      }))

      if (onChange) {
        onChange(updatedCommitments)
      }
    }
  }

  const startEditing = (key, field = null) => {
    // Store original data for potential cancel (both modes)
    const originalCommitment = commitments.find((commitment) => commitment.key === key)
    if (originalCommitment) {
      setOriginalCommitmentData({ ...originalCommitment })
    }

    setEditingRowKey(key)
    setFocusField(field)
  }

  const cancelEditing = () => {
    if (editingRowKey) {
      const isUnsavedNewEntry = newlyAddedCommitments.has(editingRowKey)

      if (isUnsavedNewEntry) {
        // Remove the new entry from the table
        const updatedCommitments = commitments.filter((commitment) => commitment.key !== editingRowKey)
        setCommitments(updatedCommitments)

        // Remove from tracking
        setNewlyAddedCommitments((prev) => {
          const newSet = new Set(prev)
          newSet.delete(editingRowKey)
          return newSet
        })

        // Notify parent of changes (for both modes)
        if (onChange) {
          onChange(updatedCommitments)
        }
      } else if (originalCommitmentData) {
        // Restore original data
        const updatedCommitments = commitments.map((commitment) =>
          commitment.key === editingRowKey ? originalCommitmentData : commitment
        )
        setCommitments(updatedCommitments)

        // Notify parent of changes (for both modes)
        if (onChange) {
          onChange(updatedCommitments)
        }
      }
    }

    // Clear field values for this row
    setFieldValues((prev) => {
      const newValues = { ...prev }
      delete newValues[`${editingRowKey}-fund`]
      delete newValues[`${editingRowKey}-amount`]
      return newValues
    })

    setEditingRowKey(null)
    setFocusField(null)
    setOriginalCommitmentData(null)

    // Scroll to left and focus Add Commitment button
    scrollToLeftAndFocus()
  }

  const saveRow = async (key) => {
    const commitmentToSave = commitments.find((commitment) => commitment.key === key)

    if (!commitmentToSave) {
      return false
    }

    // Apply field values to commitment record for validation
    const fundValue = fieldValues[`${key}-fund`] ?? commitmentToSave.fund
    const amountValue = fieldValues[`${key}-amount`] ?? commitmentToSave.amount

    const recordToValidate = {
      ...commitmentToSave,
      fund: fundValue,
      amount: amountValue
    }

    // Use new validation system with field focusing
    const validationResult = validateAndShowErrors(recordToValidate, COMMON_VALIDATION_SETS.COMMITMENT, (fieldName) => {
      // Focus field callback
      if (fieldName === 'fund') {
        // For fund field, directly trigger dropdown opening
        setTimeout(() => {
          const fundSelector = document.querySelector(`[data-testid="commitment-fund-${key}"] .ant-select-selector`)
          if (fundSelector) {
            fundSelector.click()
            fundSelector.focus()
          } else {
            setFocusField(fieldName)
          }
        }, 100)
      } else {
        // For other fields, use normal focus
        setTimeout(() => {
          setFocusField(fieldName)
        }, TIMING.DEBOUNCE_DELAY)
      }
    })

    if (!validationResult.isValid) {
      // Update validation errors state to trigger field highlighting
      const errorKey = `${key}-errors`
      setValidationErrors((prev) => ({
        ...prev,
        [errorKey]: validationResult.errors
      }))
      return false
    }

    // Clear validation errors on successful validation
    const errorKey = `${key}-errors`
    setValidationErrors((prev) => ({
      ...prev,
      [errorKey]: {}
    }))

    // Apply field values to the commitment record before saving
    const updatedCommitments = commitments.map((commitment) => {
      if (commitment.key === key) {
        return {
          ...commitment,
          fund: fundValue,
          amount: amountValue
        }
      }
      return commitment
    })
    setCommitments(updatedCommitments)

    // In new mode, just notify parent and exit editing (no API call)
    if (mode === 'new') {
      // Notify parent of the current state for queuing
      if (onChange) {
        onChange(updatedCommitments)
      }

      // Remove from newly added tracking and clear field values
      setNewlyAddedCommitments((prev) => {
        const newSet = new Set(prev)
        newSet.delete(key)
        return newSet
      })

      setFieldValues((prev) => {
        const newValues = { ...prev }
        delete newValues[`${key}-fund`]
        delete newValues[`${key}-amount`]
        return newValues
      })

      // Exit editing mode
      setEditingRowKey(null)
      setFocusField(null)
      setOriginalCommitmentData(null)

      // Scroll to left and focus Add Commitment button
      scrollToLeftAndFocus()
      return true
    }

    // Save logic - handle API call directly for existing mode
    if (mode === 'existing' && recordId) {
      try {
        setSavingRowKey(key)

        // Transform commitment to schema format
        const commitmentToSaveWithValues = {
          ...commitmentToSave,
          fund: fundValue,
          amount: amountValue
        }
        const isNewCommitmentEntry = newlyAddedCommitments.has(key)
        let schemaFormattedCommitment
        try {
          schemaFormattedCommitment = transformCommitmentToSchemaFormat(
            commitmentToSaveWithValues,
            isNewCommitmentEntry
          )
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to transform commitment'
          showErrorToast(`Commitment transformation failed: ${errorMessage}`, TOAST_CONFIG)
          return false
        }

        // Validate using Zod schema
        try {
          validateDealCommitmentEntry(schemaFormattedCommitment)
        } catch (error) {
          console.error('Validation failed for commitment entry:', error)
          const errorMessage = error instanceof Error ? error.message : 'Validation failed'
          showErrorToast(`Commitment validation failed: ${errorMessage}`, TOAST_CONFIG)
          return false
        }

        // Transform all commitments to schema format before sending to API
        const schemaFormattedCommitments = updatedCommitments.map((commitment) => {
          try {
            const isNew = newlyAddedCommitments.has(commitment.key)
            return transformCommitmentToSchemaFormat(commitment, isNew)
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to transform commitment'
            throw new Error(`Commitment transformation failed: ${errorMessage}`)
          }
        })

        // Validate all commitments
        try {
          schemaFormattedCommitments.forEach((commitment) => {
            validateDealCommitmentEntry(commitment)
          })
        } catch (error) {
          console.error('Validation failed for commitments:', error)
          const errorMessage = error instanceof Error ? error.message : 'Validation failed'
          showErrorToast(`Commitments validation failed: ${errorMessage}`, TOAST_CONFIG)
          return false
        }

        const currentToken = getAccessToken(store.getState().auth)
        const result = await updateDealCommitments(recordId, schemaFormattedCommitments, currentToken)

        if (result.error) {
          showErrorToast('Failed to save commitment', TOAST_CONFIG)
          throw new Error(result.error)
        } else {
          // Update local state with server response if available
          if (result.data) {
            // Map server response (DealFields format) to local state format
            const serverCommitments = result.data.map((serverCommitment, index) => ({
              key: serverCommitment[DealCommitmentEntryFields.deal_commitments__id] || `commitment_${index}`,
              fund: serverCommitment[DealCommitmentEntryFields.deal_commitments__fund_id],
              amount: serverCommitment[DealCommitmentEntryFields.deal_commitments__amount]
            }))
            setCommitments(serverCommitments)

            if (onChange) {
              onChange(serverCommitments)
            }

            // Map server response to DealFields format for Redux
            const commitmentUpdates = {
              [DealFields.prog__deal_commitments]: result.data.map((commitment) => ({
                [DealCommitmentEntryFields.deal_commitments__id]:
                  commitment[DealCommitmentEntryFields.deal_commitments__id],
                [DealCommitmentEntryFields.deal_commitments__fund_id]:
                  commitment[DealCommitmentEntryFields.deal_commitments__fund_id],
                [DealCommitmentEntryFields.deal_commitments__amount]:
                  commitment[DealCommitmentEntryFields.deal_commitments__amount]
              }))
            }

            // Recalculate commitment amount
            const commitmentAmount = result.data.reduce(
              (sum, commitment) => sum + (commitment[DealCommitmentEntryFields.deal_commitments__amount] || 0),
              0
            )
            commitmentUpdates[DealFields.prog__commitment_amount] = commitmentAmount

            // Update deal record for Full form display
            if (currentDeal && recordId) {
              dispatch(
                updateDealRecord({
                  dealId: recordId.toString(),
                  updates: commitmentUpdates
                })
              )
            }
          }

          toast.success('Commitment saved successfully', TOAST_SUCCESS_CONFIG)
        }
      } catch (error) {
        console.error('Failed to save commitment:', error)
        showErrorToast('Failed to save commitment', TOAST_CONFIG)
        return false
      } finally {
        setSavingRowKey(null)
      }
    }

    // Remove from newly added tracking since it's now saved
    setNewlyAddedCommitments((prev) => {
      const newSet = new Set(prev)
      newSet.delete(key)
      return newSet
    })

    // Clear field values and exit editing
    setFieldValues((prev) => {
      const newValues = { ...prev }
      delete newValues[`${key}-fund`]
      delete newValues[`${key}-amount`]
      return newValues
    })

    setEditingRowKey(null)
    setFocusField(null)
    setOriginalCommitmentData(null)

    // Scroll to left and focus Add Commitment button
    scrollToLeftAndFocus()
    return true
  }

  // Check if we should disable the "Add" button when already adding a new commitment
  const isAddingNewCommitment = editingRowKey && newlyAddedCommitments.has(editingRowKey)

  // Calculate total commitment amount - only include saved/confirmed commitments
  const totalAmount = useMemo(() => {
    return commitments
      .filter((commitment) => {
        // Exclude entries that are currently being edited (not yet saved)
        if (editingRowKey === commitment.key) {
          return false
        }
        // Include all other entries (saved or not in new mode)
        return true
      })
      .reduce((sum, commitment) => sum + (Number(commitment.amount) || 0), 0)
  }, [commitments, editingRowKey])

  // Check if there are any pending (unsaved) commitments
  const hasPendingCommitments = () => {
    return newlyAddedCommitments.size > 0
  }

  return {
    commitments,
    editingRowKey,
    focusField,
    fieldValues,
    validationErrors,
    isAddingNewCommitment,
    savingRowKey,
    deletingRowKey,
    newlyAddedCommitments,
    totalAmount,
    currencySymbol,
    getAvailableFundOptions,
    showAllFunds,
    setShowAllFunds,
    hiddenFundCount,
    handleAddCommitment,
    handleRemoveCommitment,
    handleFieldChange,
    startEditing,
    cancelEditing,
    saveRow,
    hasPendingCommitments
  }
}
